import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import type { LeaderboardRow } from '../types';

const PAGE_SIZE = 25;

export function RankingsPage() {
  const [rows, setRows] = useState<LeaderboardRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const result = await api.getLeaderboard(PAGE_SIZE, page);
        if (!cancelled) {
          setRows(result.results);
          setTotal(result.total);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load leaderboard');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      row.username.toLowerCase().includes(q) || (row.display_name || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const globalOffset = (page - 1) * PAGE_SIZE;

  return (
    <section className="panel page-stack">
      <div className="panel__header panel__header--stack">
        <div>
          <p className="eyebrow">Leaderboard</p>
          <h1>Top FTF collectors</h1>
        </div>
        <input
          className="search-input"
          type="search"
          placeholder="Filter by username"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {loading ? <div className="empty-state">Loading leaderboard...</div> : null}
      {error ? <div className="empty-state">{error}</div> : null}

      {!loading && !error ? (
        <>
          <div className="catalog-summary">
            <span>{total.toLocaleString()} total players</span>
            <span>Page {page} of {totalPages}</span>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Player</th>
                  <th>Total</th>
                  <th>Unique</th>
                  <th>Mythicals</th>
                  <th>Legendaries</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, index) => (
                  <tr key={row.roblox_id}>
                    <td>#{globalOffset + index + 1}</td>
                    <td>
                      <div className="table-player">
                        {row.headshot_url
                          ? <img className="table-avatar" src={row.headshot_url} alt={row.username} />
                          : <div className="table-avatar table-avatar--placeholder" />}
                        <div>
                          <Link className="table-link" to={`/players/${row.roblox_id}`}>
                            {row.display_name || row.username}
                          </Link>
                          <div className="table-subcopy">@{row.username}</div>
                        </div>
                      </div>
                    </td>
                    <td>{row.total_items.toLocaleString()}</td>
                    <td>{row.unique_items.toLocaleString()}</td>
                    <td>{row.mythicals.toLocaleString()}</td>
                    <td>{row.legendaries.toLocaleString()}</td>
                  </tr>
                ))}
                {filtered.length === 0 ? <tr><td colSpan={6} className="table-empty">No players matched that filter.</td></tr> : null}
              </tbody>
            </table>
          </div>
          <div className="pager">
            <button className="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span className="pager__info">Page {page} of {totalPages}</span>
            <button className="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </>
      ) : null}
    </section>
  );
}

